import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ArrowLeft, FilePdf, Trash, Plus, Receipt, Warning, Check } from '@phosphor-icons/react';
import SignaturePad from '../components/SignaturePad';
import Logo from '../components/Logo';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function ReceiptsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [view, setView] = useState('list'); // list | create
  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savedSignature, setSavedSignature] = useState(null);
  const [signatureFromSaved, setSignatureFromSaved] = useState(false);
  const sigPadRef = useRef(null);

  const [form, setForm] = useState({
    receipt_number: '',
    receipt_date: '',
    locatore_nome: '',
    locatore_cf: '',
    ospite_nome: '',
    ospite_cf: '',
    indirizzo: '',
    dal: '',
    al: '',
    canone: '',
    pulizie: '',
    tassa_soggiorno: '',
    luogo_data: '',
  });

  useEffect(() => {
    fetchReceipts();
    fetchOwnerData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchReceipts = async () => {
    try {
      const res = await axios.get(`${API}/receipts`);
      setReceipts(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchOwnerData = async () => {
    try {
      const res = await axios.get(`${API}/owner-data`);
      if (res.data) {
        setForm(f => ({
          ...f,
          locatore_nome: res.data.full_name || '',
          locatore_cf: res.data.tax_code || '',
        }));
      }
    } catch (err) {
      // Owner data not set yet
    }
    // Carica firma salvata (se presente) — endpoint /owner/profile la espone decifrata
    try {
      const r = await axios.get(`${API}/owner/profile`);
      if (r.data && r.data.signature) {
        setSavedSignature(r.data.signature);
      }
    } catch { /* nessuna firma salvata */ }
  };

  const useSavedSignature = () => {
    if (!savedSignature) return;
    setSignatureFromSaved(true);
    toast.success('Firma salvata applicata');
  };

  const clearSignature = () => {
    sigPadRef.current?.clear();
    setSignatureFromSaved(false);
  };

  const calcTotal = () => {
    const c = parseFloat((form.canone || '0').replace(',', '.')) || 0;
    const p = parseFloat((form.pulizie || '0').replace(',', '.')) || 0;
    const t = parseFloat((form.tassa_soggiorno || '0').replace(',', '.')) || 0;
    return (c + p + t).toFixed(2).replace('.', ',');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Canone NON più obbligatorio (es. ricevuta solo per tassa di soggiorno).
    // Resta obbligatorio almeno UN importo > 0 fra canone, pulizie, tassa_soggiorno.
    if (!form.locatore_nome || !form.ospite_nome || !form.indirizzo || !form.dal || !form.al) {
      toast.error('Compila locatore, ospite, indirizzo e date');
      return;
    }
    const totalNum = parseFloat((calcTotal() || '0').replace(',', '.')) || 0;
    if (totalNum <= 0) {
      toast.error('Inserisci almeno un importo (canone, pulizie o tassa di soggiorno)');
      return;
    }
    if (!signatureFromSaved && sigPadRef.current?.isEmpty()) {
      toast.error('Firma richiesta');
      return;
    }

    const totale = calcTotal();
    const firma = signatureFromSaved
      ? savedSignature
      : sigPadRef.current.toDataURL('image/png');

    try {
      const res = await axios.post(`${API}/receipts`, {
        ...form,
        canone: form.canone || '0,00',
        totale,
        firma_locatore: firma,
      });
      toast.success('Ricevuta creata!');
      // Download PDF immediately
      const pdfRes = await axios.get(`${API}/receipts/${res.data.id}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([pdfRes.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `ricevuta_${form.receipt_number || 'new'}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);

      setView('list');
      fetchReceipts();
      resetForm();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore nella creazione');
    }
  };

  const resetForm = () => {
    setForm({
      receipt_number: '',
      receipt_date: '',
      locatore_nome: form.locatore_nome,
      locatore_cf: form.locatore_cf,
      ospite_nome: '',
      ospite_cf: '',
      indirizzo: '',
      dal: '',
      al: '',
      canone: '',
      pulizie: '',
      tassa_soggiorno: '',
      luogo_data: '',
    });
    sigPadRef.current?.clear();
  };

  const downloadPdf = async (id, num) => {
    try {
      const res = await axios.get(`${API}/receipts/${id}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `ricevuta_${num || id}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('Errore nel download');
    }
  };

  const deleteReceipt = async (id) => {
    if (!window.confirm('Eliminare questa ricevuta?')) return;
    try {
      await axios.delete(`${API}/receipts/${id}`);
      toast.success('Ricevuta eliminata');
      fetchReceipts();
    } catch (err) {
      toast.error('Errore');
    }
  };

  const totalNum = parseFloat(calcTotal().replace(',', '.'));

  // ── LIST VIEW ──
  if (view === 'list') {
    return (
      <div className="min-h-screen bg-white">
        <header className="border-b border-zinc-100 px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/dashboard')} className="text-zinc-400 hover:text-zinc-900">
              <ArrowLeft size={20} />
            </button>
            <Logo size="sm" />
          </div>
          <span className="text-xs text-zinc-400">{user?.email}</span>
        </header>

        <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-2xl font-black tracking-tight" data-testid="receipts-title">Ricevute Affitto</h1>
              <p className="text-sm text-zinc-500 mt-1">Le tue ricevute sono sempre disponibili in cloud</p>
            </div>
            <Button
              onClick={() => setView('create')}
              className="rounded-none bg-zinc-900 hover:bg-zinc-800 text-white font-bold text-sm"
              data-testid="new-receipt-btn"
            >
              <Plus size={18} className="mr-2" />
              Nuova ricevuta
            </Button>
          </div>

          {loading ? (
            <p className="text-zinc-400 text-sm">Caricamento...</p>
          ) : receipts.length === 0 ? (
            <div className="text-center py-16 border border-dashed border-zinc-200">
              <Receipt size={40} className="mx-auto text-zinc-300 mb-4" />
              <p className="text-zinc-500 text-sm">Nessuna ricevuta creata</p>
              <p className="text-zinc-400 text-xs mt-1">Crea la tua prima ricevuta in 20 secondi</p>
            </div>
          ) : (
            <div className="space-y-3">
              {receipts.map((r) => (
                <div key={r.id} className="border border-zinc-200 p-4 flex items-center justify-between hover:border-zinc-400 transition-colors" data-testid={`receipt-${r.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3">
                      <span className="font-bold text-sm text-zinc-900">N° {r.receipt_number || '-'}</span>
                      <span className="text-xs text-zinc-400">{r.receipt_date}</span>
                    </div>
                    <p className="text-sm text-zinc-600 mt-1 truncate">{r.ospite_nome} &mdash; {r.indirizzo}</p>
                    <p className="text-xs text-zinc-400 mt-0.5">{r.dal} &rarr; {r.al} &middot; Totale: &euro;{r.totale}</p>
                  </div>
                  <div className="flex items-center gap-2 ml-4">
                    <Button variant="outline" size="sm" onClick={() => downloadPdf(r.id, r.receipt_number)} className="rounded-none" data-testid={`download-receipt-${r.id}`}>
                      <FilePdf size={16} />
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => deleteReceipt(r.id)} className="rounded-none text-red-500 hover:text-red-700" data-testid={`delete-receipt-${r.id}`}>
                      <Trash size={16} />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </main>
      </div>
    );
  }

  // ── CREATE VIEW ──
  return (
    <div className="min-h-screen bg-white">
      <header className="border-b border-zinc-100 px-4 sm:px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button onClick={() => setView('list')} className="text-zinc-400 hover:text-zinc-900">
            <ArrowLeft size={20} />
          </button>
          <Logo size="sm" />
        </div>
        <span className="text-xs text-zinc-400">Nuova ricevuta</span>
      </header>

      <main className="max-w-2xl mx-auto px-4 sm:px-6 py-8">
        <h1 className="text-xl font-black tracking-tight mb-6" data-testid="create-receipt-title">Crea Ricevuta Affitto</h1>

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Header fields */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-bold text-zinc-500 uppercase tracking-wider">N° Ricevuta</label>
              <Input value={form.receipt_number} onChange={e => setForm({...form, receipt_number: e.target.value})} className="rounded-none mt-1" placeholder="001" data-testid="receipt-number-input" />
            </div>
            <div>
              <label className="text-xs font-bold text-zinc-500 uppercase tracking-wider">Data</label>
              <Input type="date" value={form.receipt_date} onChange={e => setForm({...form, receipt_date: e.target.value})} className="rounded-none mt-1" required data-testid="receipt-date-input" />
            </div>
          </div>

          {/* Locatore e Ospite */}
          <div>
            <h2 className="text-sm font-black uppercase tracking-wider text-zinc-900 mb-3 pb-2 border-b border-zinc-200">Dati Locatore e Conduttore</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-bold text-zinc-500 uppercase tracking-wider">Locatore *</label>
                <Input value={form.locatore_nome} onChange={e => setForm({...form, locatore_nome: e.target.value})} className="rounded-none mt-1" required data-testid="locatore-nome-input" />
              </div>
              <div>
                <label className="text-xs font-bold text-zinc-500 uppercase tracking-wider">Ospite *</label>
                <Input value={form.ospite_nome} onChange={e => setForm({...form, ospite_nome: e.target.value})} className="rounded-none mt-1" required data-testid="ospite-nome-input" />
              </div>
              <div>
                <label className="text-xs font-bold text-zinc-500 uppercase tracking-wider">Cod. Fiscale Locatore</label>
                <Input value={form.locatore_cf} onChange={e => setForm({...form, locatore_cf: e.target.value})} className="rounded-none mt-1" data-testid="locatore-cf-input" />
              </div>
              <div>
                <label className="text-xs font-bold text-zinc-500 uppercase tracking-wider">Cod. Fiscale Ospite</label>
                <Input value={form.ospite_cf} onChange={e => setForm({...form, ospite_cf: e.target.value})} className="rounded-none mt-1" data-testid="ospite-cf-input" />
              </div>
            </div>
          </div>

          {/* Immobile e Periodo */}
          <div>
            <h2 className="text-sm font-black uppercase tracking-wider text-zinc-900 mb-3 pb-2 border-b border-zinc-200">Dettaglio Immobile e Periodo</h2>
            <div className="space-y-4">
              <div>
                <label className="text-xs font-bold text-zinc-500 uppercase tracking-wider">Indirizzo immobile *</label>
                <Input value={form.indirizzo} onChange={e => setForm({...form, indirizzo: e.target.value})} className="rounded-none mt-1" required data-testid="indirizzo-input" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-bold text-zinc-500 uppercase tracking-wider">Dal *</label>
                  <Input type="date" value={form.dal} onChange={e => setForm({...form, dal: e.target.value})} className="rounded-none mt-1" required data-testid="dal-input" />
                </div>
                <div>
                  <label className="text-xs font-bold text-zinc-500 uppercase tracking-wider">Al *</label>
                  <Input type="date" value={form.al} onChange={e => setForm({...form, al: e.target.value})} className="rounded-none mt-1" required data-testid="al-input" />
                </div>
              </div>
            </div>
          </div>

          {/* Importi */}
          <div>
            <h2 className="text-sm font-black uppercase tracking-wider text-zinc-900 mb-3 pb-2 border-b border-zinc-200">Importi</h2>
            <p className="text-[11px] text-zinc-500 mb-3">
              Compila almeno uno fra canone, pulizie e tassa di soggiorno. Per ricevute solo della tassa di soggiorno, lascia il canone a 0.
            </p>
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <label className="text-sm text-zinc-700 w-48">Canone locazione breve</label>
                <div className="flex items-center gap-1 flex-1">
                  <span className="text-zinc-400 text-sm">&euro;</span>
                  <Input value={form.canone} onChange={e => setForm({...form, canone: e.target.value})} className="rounded-none" placeholder="0,00 (opzionale)" data-testid="canone-input" />
                </div>
              </div>
              <div className="flex items-center gap-3">
                <label className="text-sm text-zinc-700 w-48">Pulizie e costi accessori</label>
                <div className="flex items-center gap-1 flex-1">
                  <span className="text-zinc-400 text-sm">&euro;</span>
                  <Input value={form.pulizie} onChange={e => setForm({...form, pulizie: e.target.value})} className="rounded-none" placeholder="0,00" data-testid="pulizie-input" />
                </div>
              </div>
              <div className="flex items-center gap-3">
                <label className="text-sm text-zinc-700 w-48">Tassa di soggiorno</label>
                <div className="flex items-center gap-1 flex-1">
                  <span className="text-zinc-400 text-sm">&euro;</span>
                  <Input value={form.tassa_soggiorno} onChange={e => setForm({...form, tassa_soggiorno: e.target.value})} className="rounded-none" placeholder="0,00" data-testid="tassa-input" />
                </div>
              </div>
              <div className="flex items-center gap-3 pt-3 border-t border-zinc-200">
                <label className="text-sm font-black text-zinc-900 w-48">TOTALE RICEVUTO</label>
                <div className="flex items-center gap-1 flex-1">
                  <span className="text-zinc-900 font-bold text-sm">&euro;</span>
                  <span className="text-xl font-black text-zinc-900" data-testid="totale-display">{calcTotal()}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Marca da bollo warning */}
          {totalNum > 77.47 && (
            <div className="bg-amber-50 border-2 border-amber-300 p-4 flex items-start gap-3" data-testid="marca-bollo-warning">
              <Warning size={24} weight="fill" className="text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-bold text-amber-900 text-sm">Marca da bollo obbligatoria</h3>
                <p className="text-amber-700 text-xs mt-1">
                  L'importo supera &euro;77,47. Dopo aver stampato la ricevuta, applica una marca da bollo da &euro;2,00. 
                  La marca deve riportare una data di emissione <strong>antecedente o uguale</strong> alla data della ricevuta.
                </p>
              </div>
            </div>
          )}

          {/* Luogo e data */}
          <div>
            <label className="text-xs font-bold text-zinc-500 uppercase tracking-wider">Luogo e data</label>
            <Input value={form.luogo_data} onChange={e => setForm({...form, luogo_data: e.target.value})} className="rounded-none mt-1" placeholder="Roma, 10/04/2026" data-testid="luogo-data-input" />
          </div>

          {/* Firma */}
          <div>
            <h2 className="text-sm font-black uppercase tracking-wider text-zinc-900 mb-3 pb-2 border-b border-zinc-200">Firma del Locatore</h2>
            {savedSignature && !signatureFromSaved && (
              <div className="bg-emerald-50 border border-emerald-200 p-3 flex items-center justify-between gap-3 flex-wrap mb-3" data-testid="saved-signature-card">
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <img src={savedSignature} alt="Firma salvata" className="h-10 bg-white border border-emerald-100 px-2" />
                  <p className="text-[11px] font-bold uppercase tracking-wider text-emerald-800">Firma precompilata dal profilo</p>
                </div>
                <Button
                  type="button"
                  onClick={useSavedSignature}
                  className="rounded-none bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold uppercase tracking-wider h-8 px-3"
                  data-testid="use-saved-signature-btn"
                >
                  <Check size={13} className="mr-1" /> Usa firma salvata
                </Button>
              </div>
            )}
            {signatureFromSaved ? (
              <div className="border-2 border-emerald-500 bg-emerald-50 p-4 flex items-center justify-between gap-3 flex-wrap" data-testid="signature-from-saved">
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <Check size={20} weight="bold" className="text-emerald-700" />
                  <img src={savedSignature} alt="Firma applicata" className="h-12 bg-white border border-emerald-200 px-3" />
                  <p className="text-xs font-bold text-emerald-800">Firma del profilo applicata · pronta per il PDF</p>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setSignatureFromSaved(false)}
                  className="rounded-none text-xs"
                  data-testid="switch-to-pad-btn"
                >
                  Firma a mano →
                </Button>
              </div>
            ) : (
              <div className="border border-zinc-300 bg-zinc-50">
                <SignaturePad
                  ref={sigPadRef}
                  height={200}
                  data-testid="signature-canvas"
                />
              </div>
            )}
            {!signatureFromSaved && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={clearSignature}
                className="rounded-none mt-2 text-xs"
                data-testid="clear-signature-btn"
              >
                Cancella firma
              </Button>
            )}
          </div>

          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => { setView('list'); resetForm(); }}
              className="rounded-none"
            >
              Annulla
            </Button>
            <Button
              type="submit"
              className="rounded-none bg-zinc-900 hover:bg-zinc-800 text-white font-bold flex-1"
              data-testid="create-receipt-submit"
            >
              <FilePdf size={18} className="mr-2" />
              Crea e Scarica PDF
            </Button>
          </div>
        </form>
      </main>
    </div>
  );
}
